// PostHog web analytics for the signed-in dashboard. Six events are captured
// client-side:
//   sign_in, client_created, site_created,
//   issue_status_changed, checklist_updated, checkout_started
// extension_connected is deliberately NOT among them: it is captured server-side
// in routes.py, at the moment the extension actually redeems the pairing code.
// Firing it here as well counted every pairing twice in the activation funnel.
//
// Autocapture and automatic pageviews stay off: this is a dashboard behind a
// login, and pageview volume here answers no question the events do not.
//
// persistence and cross_subdomain_cookie must match landing/src/lib/analytics.js.
// They put the distinct_id on a .complaudax.com cookie, so a visitor who did
// trigger an event on the marketing site keeps the same anonymous id here.
//
// That only covers visitors who triggered an event, and posthog-js does not load
// on the marketing site otherwise. The first-touch referrer and campaign of
// everyone else travel in a separate cookie written without any bundle at all;
// identify() below reads it through lib/attribution.js and attaches it as
// $set_once, which is what makes an organic signup attributable.
// No-ops when the key is unset.
import posthog from "posthog-js";
import { attributionProps } from "./attribution";

const KEY = process.env.REACT_APP_POSTHOG_KEY;
let ready = false;

if (KEY) {
  posthog.init(KEY, {
    api_host: process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com",
    defaults: "2026-05-30",
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    persistence: "localStorage+cookie",
    cross_subdomain_cookie: true,
  });
  ready = true;
}

export const track = (event, properties = {}) => {
  if (ready) posthog.capture(event, properties);
};

/* Takes the raw email and stores only its domain. Deriving it here rather than
 * at the call site is the point: there is no argument shape that leaks an
 * address to PostHog by accident. The domain is what the analytics questions
 * are actually about ("are agencies signing up?"), and it matches the
 * email_domain property the backend already sets on sign_up.
 *
 * The third argument is $set_once: first-touch attribution read from the cookie
 * the marketing site wrote. It lands on the person the first time only, so the
 * search that produced the account cannot be overwritten by a later sign-in. */
export const identify = (id, email = "") => {
  if (!ready) return;
  const domain = String(email).split("@")[1] || "";
  posthog.identify(id, domain ? { email_domain: domain } : {}, attributionProps());
};

export const resetAnalytics = () => {
  if (ready) posthog.reset();
};

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign"];
const UTM_STORE = "complaudax_utm";

export const captureUtmsFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const found = {};
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) found[k] = v;
  });
  if (Object.keys(found).length) {
    try { localStorage.setItem(UTM_STORE, JSON.stringify(found)); } catch { /* storage unavailable */ }
  }
};

export const consumeUtms = () => {
  let raw = null;
  try { raw = localStorage.getItem(UTM_STORE); } catch { return {}; }
  if (!raw) return {};
  try { localStorage.removeItem(UTM_STORE); } catch { /* ignore */ }
  try { return JSON.parse(raw); } catch { return {}; }
};
